/*
 *
 * Copyright 2022, White River Computing LLC. All rights reserved.
 * For licensing information, please contact Charlie Flock (cflock@whiterivercomputing.com or 888-802-6340)
 */
import './WrcSpecimenReports.css';

import React from "react";
import PropTypes from "prop-types";

import {Card, Elevation, HTMLTable} from "@blueprintjs/core";
import {Icon, Spinner, NonIdealState} from "@blueprintjs/core";
import {Tabs, Tab} from "@blueprintjs/core";
import * as IconNames from "@blueprintjs/icons/lib/esm/generated/iconNames";

import {Chart, Pies, Transform} from 'rumble-charts';

import {WrcCardHeader} from "./WrcCardHeader";
import {WrcServer} from "./WrcServer";
import {WrcColors} from "./WrcBranding";

export class SpecimenReportTab extends React.PureComponent {

    toggleDrilldown(e, data) {
        e.preventDefault();
        if (this.props.getDrilldownStatus() && data.drilldown_value === this.props.getDrilldownValue()) {
            this.props.toggleDrilldownStatus()
        } else {
            this.props.specimenDrilldown(data)
        }
    }

    getClickableRow = (title, short_title) => {
        if (this.props.content.column_groupby) {
            return (<a href="#" onClick={(e) => this.toggleDrilldown(e, {...this.props.content, 'drilldown_value': title, 
            'specimen_table': this.props.specimenTable, 'drilldown_tally_header': this.props.drilldownTallyHeader,
            'specimen_schema': this.props.specimenSchema, 'specimen_type_source_column': this.props.specimenTypeSourceColumn})}>{short_title}</a>)
        }
        return short_title
    }

    parseTableData(title, accdata, total, label) {
        let tablerows = [], keyname = title + '-' + label;
        for (let i = 0; i < accdata.length; i++) {
            let acc_tabrow = accdata[i];
            if (acc_tabrow.y || 
                (this.props.content.valid_cohorts && this.props.content.valid_cohorts.includes(acc_tabrow.title))
                ) {
                tablerows.push(
                    <tr key={'acc-tabrow-' + keyname + '-' + i}>
                        <td style={{whiteSpace: 'nowrap'}}><Icon icon={IconNames.SYMBOL_SQUARE}
                                color={acc_tabrow.color}
                                style={{marginRight: '.2em'}}/>
                            {this.getClickableRow(acc_tabrow.title, acc_tabrow.short_title)}
                        </td>
                        <td>{acc_tabrow.y}</td>
                        <td>{acc_tabrow.parent}</td>
                        <td>{acc_tabrow.derived}</td>
                    </tr>
                );
            }
        }

        tablerows.push(
            <tr key={'acc-tabrow-totals-' + keyname}>
                <td>Total</td>
                <td>{this.props.content.total}</td>
                <td>{this.props.content.total_parent}</td>
                <td>{this.props.content.total_derived}</td>
            </tr>
        );

        return tablerows;
    }

    constructor(props) {
        super(props);
        this.parseTableData = this.parseTableData.bind(this)
        this.toggleDrilldown = this.toggleDrilldown.bind(this)
        const thedata = props.content,
            tot_all = thedata.total;

        let alldata = [], thecolor = 0, chart_data = [];
        if (thedata.responses) {
            for (let [key, value] of Object.entries(thedata.responses)) {
                const color = WrcColors.CHART_COLORS_L1[(thecolor++) % WrcColors.CHART_COLORS_L1.length];
                const chart_total = value['derived'] + value['parent']
                alldata.push({title: key, short_title: props.cohort_short_names[key] ? props.cohort_short_names[key] : key, y: value['total'], color: color, 
                              parent: String(value['parent']), derived: String(value['derived'])});
                chart_data.push({title: key, short_title: props.cohort_short_names[key] ? props.cohort_short_names[key] : key, y: chart_total, color: color, 
                              parent: String(value['parent']), derived: String(value['derived'])})
            }
        }
        const taball = this.parseTableData(props.content.title + '-all', alldata, tot_all, 'All Sites');
        this.state = {
            acc_data_all: [{ data: alldata }],
            chart_data: [{data: chart_data}],
            cur_data:  [{ data: alldata }],
            table_data_all: taball,
            cur_table: taball,
            tot_all: tot_all
        };
    }

    render() {
        const thetitle =
            <span>
                {this.props.content.title + ' Specimens'}
            </span>;

        let renderedChart=undefined;
        if(this.state.tot_all === 0){
            renderedChart=(
                <NonIdealState className='non-ideal-state' icon='pie-chart' title='No Specimen Data Accrued' description='Please add specimen data for the selected category to see specimen report.'/>
            );
        } else {
            renderedChart= (
                <Chart width={140} height={140} series={this.state.chart_data}  className='pie-chart'>
                    <Transform method={['transpose', 'stack']}>
                        <Pies combined={true} />
                    </Transform>
                </Chart>
            );
        }
        const column_header = this.props.content.column_header || ' ';
        const tally_header = this.props.content.tally_header || 'Total';
        return (
            <Card className='report-card' interactive={false} style={{minHeight:'570px'}}>
                <WrcCardHeader title={thetitle}/>
                <div className ='summary-chart-container'>
                    {renderedChart}
                </div>

                <div className='summary-table-container'>
                    <HTMLTable condensed={true} bordered={true}
                        key={'accrual-table-' + thetitle}
                        className='summary-table'>
                        <thead>
                        <tr>
                            <th>{column_header}</th>
                            <th>{tally_header}</th>
                            <th>Specimens (parent)</th>
                            <th>Specimens (derived)</th>
                        </tr>
                        </thead>
                        <tbody>
                        {this.state.cur_table}
                        </tbody>
                    </HTMLTable>
                </div>
            </Card>
        );
    }
}

SpecimenReportTab.propTypes = {
    showreportshandler: PropTypes.func.isRequired,
    content: PropTypes.object.isRequired,
};

export class WrcSpecimenReports extends React.PureComponent {
    parseSpecimenData(thecontent) {
        let reporttabs = [];
        for (let i = 0; i < thecontent.length; i++) {
            reporttabs.push(<Tab title={<span>{thecontent[i].title}</span>} id={'report-' + i + '-tab'} key={'report-' + i + '-tab'} panel={
                <SpecimenReportTab key={'accrualtab-' + i}
                                    content={thecontent[i]}
                                    specimenDrilldown={this.specimenDrilldown}
                                    getDrilldownValue={this.getDrilldownValue}
                                    toggleDrilldownStatus={this.toggleDrilldownStatus}
                                    getDrilldownStatus={this.getDrilldownStatus}
                                    drilldownTallyHeader={thecontent[i]['drilldown_tally_header']}
                                    cohort_short_names={this.props.cohort_short_names}
                                    specimenSchema={this.props.report_child_schema}
                                    specimenTable={this.props.report_child_table}
                                    specimenTypeSourceColumn={this.props.specimen_type_source_column}
                                    />
            }/>);
        }
        this.setState({reportTabs: reporttabs});
    }

    constructor(props) {
        super(props);
        this.state = {reportTabs: []};
        this.specimenDrilldown = this.specimenDrilldown.bind(this)
    }

    componentDidMount() {
        this.parseSpecimenData(this.props.data)
    }

    capitalize(word) {
        return word[0].toUpperCase() + word.slice(1).toLowerCase();
      }

    toggleDrilldownStatus = () => {
        this.setState({cohort_selected: !this.state.cohort_selected})
    }

    getDrilldownStatus = () => {
        return this.state.cohort_selected
    }

    getDrilldownValue = () => {
        return this.state.drilldown_value
    }

    displayDrilldown() {
        let thecolor = 0;
        let table_data = this.state.drilldown_data.map((row) => {
            const icon_color = WrcColors.CHART_COLORS_L1[(thecolor++) % WrcColors.CHART_COLORS_L1.length];
            return (
                <tr>
                    <td>
                        <Icon icon={IconNames.SYMBOL_SQUARE}
                                    color={icon_color}
                                    style={{marginRight: '.2em'}}/>
                        {row.site}
                    </td>
                    <td>
                        {row.records}
                    </td>
                    <td>
                        {row.parent_count}
                    </td>
                    <td>
                        {row.derived_count}
                    </td>
                </tr>
            )
        })
        return (
        <span className='summary-table-container'>
            <HTMLTable condensed={true} bordered={true}
                className='summary-table'>
                <thead>
                <tr>
                    <th>{this.capitalize(this.state.grouped_by)}</th>
                    <th>{this.state.drilldown_tally_header}</th>
                    <th>Specimens (parent)</th>
                    <th>Specimens (derived)</th>
                </tr>
                </thead>
                <tbody>
                {table_data}
                </tbody>
            </HTMLTable>
        </span>
        )
    }

    displayChart() {
        let alldata = [], thecolor = 0, chart_data = [];
        if (this.state.drilldown_data) {
            for (let i=0; i<this.state.drilldown_data.length; i++) {
                const color = WrcColors.CHART_COLORS_L1[(thecolor++) % WrcColors.CHART_COLORS_L1.length];
                const chart_total = parseInt(this.state.drilldown_data[i]['parent_count']) + parseInt(this.state.drilldown_data[i]['derived_count'])
                // cohort, site, count
                alldata.push({title: this.state.drilldown_data[i][this.state.grouped_by], y: parseInt(this.state.drilldown_data[i]['records']), color: color, x: i});
                chart_data.push({title: this.state.drilldown_data[i][this.state.grouped_by], y: chart_total, color: color, x: i})
            }
        }
        chart_data = [{data: chart_data}]
        let renderedChart=undefined;
        if(this.state.drilldown_data.length === 0){
            renderedChart=(
                <span>
                    <NonIdealState className='non-ideal-state' icon='pie-chart' title='No Specimen Data Accrued' description='Please add specimen data for the selected category to see specimen report.'/>
                </span>
            );
        } else {
            renderedChart= (
                <span>
                    <Chart width={140} height={140} series={chart_data}  className='pie-chart'>
                        <Transform method={['transpose', 'stack']}>
                            <Pies combined={true} />
                        </Transform>
                    </Chart>
                </span>
            );
        }
        return renderedChart
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.cohort_selected !== this.state.cohort_selected) {
            if (this.state.cohort_selected) {
                document.getElementById('specimen-drilldown').scrollIntoView(false)
            }
        }
    }

    specimenDrilldown = (data) => {
        WrcServer.post('get-site-specimens',
        data,
        (resp) => {
                this.setState({
                    cohort_selected: true,
                    drilldown_cohort: data.title,
                    grouped_by: data.column_groupby,
                    drilldown_value: data.drilldown_value,
                    drilldown_tally_header: data.drilldown_tally_header,
                    drilldown_data: resp
                })
            }
        )
    }

    render() {
        let card_alignment= this.props.component_alignment === 'center' ? 'center' : 'left';
        let report_full_class= 'report-div ' + card_alignment;
        let drilldown_component = this.state.cohort_selected ?  this.state.drilldown_data.length > 0 ? (                    
        <div >  
            <Card id="specimen-drilldown" className="drilldown-card">
                <WrcCardHeader small={true} title={this.state.drilldown_cohort} />
                <WrcCardHeader small={true} title={this.state.drilldown_value} />
                <div className='rowC'>
                    {this.displayChart()}
                    {this.displayDrilldown()}
                </div>
            </Card>
        </div>) 
        : (<div><Card id="specimen-drilldown" className="drilldown-card"><NonIdealState className='non-ideal-state' icon='pie-chart' title='No Specimen Data for Site' description='Please add specimen data for the selected cohort to see site report.'/></Card></div>) 
        : (<div id="specimen-drilldown"></div>)
        if (!this.state.reportTabs.length) {
            return (
                <div className={report_full_class}>
                    <div>
                        <Spinner/>
                    </div>
                </div>
            );
        } else if (this.state.tot_site === 0) {
            return null;
        } else {
            return (
                    <div>
                        {this.state.reportTabs}
                        { drilldown_component }
                    </div>
            );
        }
    }
}

WrcSpecimenReports.propTypes = {
    reports: PropTypes.array.isRequired
};
